import { useEffect, useState } from 'react';
import { apiGet } from '@/lib/api';
import { formatCalendarDay } from '@/lib/dates';
import { Card } from '@/components/ui';

interface MeterReading {
  id: string;
  reading_hours: number;
  as_of: string;
  note: string | null;
}

/**
 * The Hobbs readings on record, read-only.
 *
 * They stay exactly as entered — the declared 10.59 h at delivery included — and
 * are reachable here without the database, on the owner's answer (2026-10). Nothing
 * computes from them: the log is on flight time now and the Hobbs balance is gone,
 * so there is deliberately no way to add or edit one on this page or any other.
 */
export default function MeterReadings() {
  const [readings, setReadings] = useState<MeterReading[] | null>(null);

  useEffect(() => {
    apiGet<{ readings: MeterReading[] }>('/api/meter')
      .then((d) => setReadings(d.readings || []))
      .catch(() => setReadings([]));
  }, []);

  return (
    <Card className="mb-6 p-5 sm:p-6">
      <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-300">Hobbs meter readings</h2>
      <p className="mt-1 text-xs text-slate-500">
        The readings on record, as entered. Kept for reference only — FlightTrack logs flight time and computes
        nothing from these.
      </p>
      {readings === null ? null : readings.length === 0 ? (
        <p className="mt-4 text-sm text-slate-400">No readings on record.</p>
      ) : (
        <ul className="mt-4 divide-y divide-white/5 text-sm">
          {readings.map((r, i) => (
            <li key={r.id} className="py-2">
              <div className="flex flex-wrap items-baseline justify-between gap-x-3">
                <span className="text-slate-300">
                  {/* A reading's as_of is a calendar day, not a moment — see lib/dates.ts. */}
                  {formatCalendarDay(r.as_of)}
                  {i === 0 && readings.length > 1 && <span className="ml-2 text-xs text-slate-500">at delivery, as declared</span>}
                </span>
                <span className="tabular-nums text-slate-200">{r.reading_hours.toFixed(2)} h</span>
              </div>
              {r.note && <p className="mt-1 break-words text-xs text-slate-500">{r.note}</p>}
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}
